import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, RefObject } from "react";
import { useLocation } from "react-router";
import { Button, ForteXMap, decode } from "@fortex/map";
import type { ForteXMapConfig, MapCoreApi, Theme } from "@fortex/map";
import { TopPill } from "../app/AppMenu";
import { useApp, useAuth } from "../app/context";
import { features } from "../auth/store";
import type { AppLang } from "../i18n";
import { langIdOf, langOfId } from "../i18n";
import { CreatePublicMapDialog } from "./CreatePublicMapDialog";
import { mapControlsFor } from "./mapFeatures";
import type { Can } from "./mapFeatures";

export const mapStorageKey = "web";

export type MapPageInput = { search: string; lang: AppLang; theme: Theme; admin: boolean; can: Can; accent: string | null; nominatimUrl: string | null; center?: [number, number] | null; zoom?: number | null };

export function mapPageConfig(input: MapPageInput): ForteXMapConfig {
  const url = decode(input.search);
  const gated = mapControlsFor(input.can);

  return {
    lang: url.lang ?? input.lang,
    theme: url.theme ?? input.theme,
    chrome: url.chrome ?? "full",
    accent: input.accent,
    storageKey: mapStorageKey,
    urlState: true,
    layers: { maps: url.maps, layersOffered: url.layers },
    info: { ...gated.info, admin: input.admin },
    view: { center: input.center ?? undefined, zoom: input.zoom ?? undefined },
    nominatimUrl: input.nominatimUrl,
    controls: gated.controls
  };
}

export function canEditMap(can: (feature: string, level: "ReadOnly" | "Full") => boolean): boolean {
  return can(features.geoObject, "Full") || can(features.mapService, "Full");
}

export function useMapAppSync(coreRef: RefObject<MapCoreApi | null>): (core: MapCoreApi) => void {
  const { lang, setLang, theme, setTheme } = useApp();

  useEffect(() => {
    const core = coreRef.current;

    if (core !== null && core.store.getState().langId !== langIdOf(lang)) core.setLangId(langIdOf(lang));
  }, [coreRef, lang]);

  useEffect(() => {
    const core = coreRef.current;

    if (core !== null && core.store.getState().theme !== theme) core.setTheme(theme);
  }, [coreRef, theme]);

  return useCallback(
    (core: MapCoreApi) => {
      coreRef.current = core;

      let lastLang = core.store.getState().langId;
      let lastTheme = core.store.getState().theme;

      setLang(langOfId(lastLang));
      setTheme(lastTheme);

      core.store.subscribe(state => {
        if (state.langId !== lastLang) {
          lastLang = state.langId;
          setLang(langOfId(state.langId));
        }

        if (state.theme !== lastTheme) {
          lastTheme = state.theme;
          setTheme(state.theme);
        }
      });
    },
    [coreRef, setLang, setTheme]
  );
}

export function MapPage() {
  const { api, config, lang, theme, labels } = useApp();
  const admin = useAuth(a => canEditMap(a.can));
  const can = useAuth(a => a.can);
  const canPublicMap = useAuth(a => a.can(features.publicMap, "Full"));
  const location = useLocation();
  const coreRef = useRef<MapCoreApi | null>(null);
  const [initial] = useState(() => ({ search: location.search, lang, theme, admin, can }));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [core, setCore] = useState<MapCoreApi | null>(null);
  const mapConfig = useMemo(() => mapPageConfig({ search: initial.search, lang: initial.lang, theme: initial.theme, admin: initial.admin, can: initial.can, accent: config.accent, nominatimUrl: config.nominatimUrl, center: config.mapCenter, zoom: config.mapZoom }), [initial, config]);
  const sync = useMapAppSync(coreRef);
  const onReady = useCallback(
    (ready: MapCoreApi) => {
      setCore(ready);
      sync(ready);
    },
    [sync]
  );

  return (
    <div className="fxapp-map-page" data-fxmap="map-page">
      <ForteXMap api={api} config={mapConfig} onReady={onReady} style={config.accent ? ({ "--fxmap-accent": config.accent } as CSSProperties) : undefined} />
      <div className="fxapp-map-chrome">
        <TopPill>{canPublicMap ? <Button kind="icon" size="sm" icon="link" label={labels.publicMapCreate} onClick={() => setDialogOpen(true)} data-fxmap="public-map-create" /> : null}</TopPill>
      </div>
      <CreatePublicMapDialog core={core} open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
